import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Container, Row, Tab, Tabs } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import _ from 'lodash';

import { resourceActions, templateActions } from '../../actions';
import CheckPermission from '../../components/checkPermission/CheckPermission';
import PageTitle from '../../components/common/PageTitle';
import AddResource from '../../components/resource/AddResource';
import { educationTaxonomyService, templateService } from '../../services';
import { authResourceUrl } from '../../utils/authResourceUrl';
import { _getCategories } from './categoriesSize';

const RESOURCE_TYPE_ORDER = [
  'Image',
  'Video',
  'Vector',
  'Shape',
  'Frame',
  'Grid',
  'Line',
  'Text',
  'Background',
  'Element',
  'Font',
  'Icon',
];

const splitTags = value => {
  if (Array.isArray(value)) return value.map(tag => String(tag).trim()).filter(Boolean);
  return String(value || '')
    .replace(/،/g, ',')
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);
};

const TemplateEdit = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { _templateId } = useParams();
  const template = useSelector(state => state.template.template);
  const loading = useSelector(state => state.template.status);
  const deleteResult = useSelector(state => state.resource.deleteResult);
  const uploadResult = useSelector(state => state.resource.uploadResult);
  const authToken = useSelector(state => state.authentication.user?.authToken);

  const [activeTab, setActiveTab] = useState('details');
  const [sendUpdate, setSendUpdate] = useState(0);
  const [taxonomy, setTaxonomy] = useState([]);
  const [gradeCode, setGradeCode] = useState('');
  const [subjectCode, setSubjectCode] = useState('');
  const [topicCodes, setTopicCodes] = useState([]);
  const [templateResources, setTemplateResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState('');
  const [deleteResource, setDeleteResource] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [tutorialVideoBusy, setTutorialVideoBusy] = useState(false);
  const [tutorialVideoMessage, setTutorialVideoMessage] = useState('');

  const fallbackGrades = useMemo(() => _getCategories().map((category, index) => ({
    _id: `fallback-${index}`,
    code: index === 0 ? 'preschool' : `grade-${index}`,
    title: category.name,
    type: 'grade',
    active: true,
  })), []);
  const grades = useMemo(() => {
    const result = taxonomy.filter(item => item.type === 'grade' && item.active);
    return result.length ? result : fallbackGrades;
  }, [fallbackGrades, taxonomy]);
  const subjects = useMemo(() => taxonomy.filter(item => item.type === 'subject' && item.active
    && (!(item.gradeCodes || []).length || item.gradeCodes.includes(gradeCode))), [gradeCode, taxonomy]);
  const topics = useMemo(() => taxonomy.filter(item => item.type === 'topic' && item.active
    && (!subjectCode || item.subjectCode === subjectCode)
    && (!(item.gradeCodes || []).length || item.gradeCodes.includes(gradeCode))), [gradeCode, subjectCode, taxonomy]);

  const groupedResources = useMemo(() => {
    const groups = templateResources.reduce((result, resource) => {
      const type = resource.type || 'Other';
      if (!result[type]) result[type] = [];
      result[type].push(resource);
      return result;
    }, {});

    return Object.entries(groups).sort(([left], [right]) => {
      const leftIndex = RESOURCE_TYPE_ORDER.indexOf(left);
      const rightIndex = RESOURCE_TYPE_ORDER.indexOf(right);
      return (leftIndex === -1 ? RESOURCE_TYPE_ORDER.length : leftIndex)
        - (rightIndex === -1 ? RESOURCE_TYPE_ORDER.length : rightIndex);
    });
  }, [templateResources]);

  const loadTemplateResources = useCallback(async () => {
    if (!template._id) return;
    setResourcesLoading(true);
    setResourcesError('');
    try {
      const resources = await templateService.getResources(template._id);
      setTemplateResources(Array.isArray(resources) ? resources : []);
    } catch (error) {
      setResourcesError(error.error || error.message || 'Unable to load template resources.');
    } finally {
      setResourcesLoading(false);
    }
  }, [template._id]);

  useEffect(() => {
    dispatch(templateActions.getById(_templateId));
    educationTaxonomyService.findAll({ active: true }).then(items => {
      setTaxonomy(Array.isArray(items) ? items : []);
    }).catch(() => setTaxonomy([]));
  }, [_templateId, dispatch]);

  useEffect(() => {
    if (!template._id) return;
    const storedGrade = _.get(template, 'education.gradeCode', '');
    const legacyTitle = _.get(template, 'categories[0]', '');
    const matchedGrade = grades.find(grade => grade.title === legacyTitle);
    setGradeCode(storedGrade || _.get(matchedGrade, 'code', _.get(grades, '[0].code', 'preschool')));
    setSubjectCode(_.get(template, 'education.subjectCode', ''));
    setTopicCodes(_.get(template, 'education.topicCodes', []) || []);
  }, [grades, template]);

  useEffect(() => {
    if (activeTab === 'resources') loadTemplateResources();
  }, [activeTab, loadTemplateResources]);

  useEffect(() => {
    if (sendUpdate !== 1) return;
    if (loading === 'success') setSendUpdate(2);
    if (loading === 'failure') setSendUpdate(-1);
  }, [loading, sendUpdate]);

  useEffect(() => {
    if (deleteResult === 'success') {
      setDeleteResource(null);
      setDeleteMessage('File deleted successfully.');
      loadTemplateResources();
    } else if (deleteResult === 'failed') {
      setDeleteMessage('File deletion failed.');
    }
  }, [deleteResult, loadTemplateResources]);

  useEffect(() => {
    if (uploadResult === 'success' && activeTab === 'resources') loadTemplateResources();
  }, [activeTab, loadTemplateResources, uploadResult]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: template.name || '',
      price: String(_.get(template, 'price', 0)),
        description: _.get(template, 'description', ''),
      tags: (_.get(template, 'tags', []) || []).join(', '),
      tagsFa: (_.get(template, 'tagsFa', []) || []).join(', '),
        invisible: String(_.get(template, 'invisible', false)),
        learningObjective: _.get(template, 'education.learningObjective', ''),
        estimatedMinutes: String(_.get(template, 'education.estimatedMinutes', 15)),
    },
    onSubmit: values => {
      setSendUpdate(1);
      dispatch(templateActions.update(_templateId, {
        name: values.name.trim(),
        price: Number(values.price),
        categories: gradeCode ? [_.get(grades.find(grade => grade.code === gradeCode), 'title', '')] : [],
        invisible: values.invisible === 'true',
        description: values.description,
        tags: splitTags(values.tags),
        tagsFa: splitTags(values.tagsFa),
        size: { width: 1600, height: 1600 },
        education: {
          mode: 'lesson',
          gradeCode,
          subjectCode: subjectCode || undefined,
          topicCodes,
          learningObjective: values.learningObjective,
          estimatedMinutes: Number(values.estimatedMinutes) || undefined,
        },
      }));
    },
  });

  const resourceThumbnail = resource => {
    const thumbnailName = resource.type === 'Video' ? 'frame' : 'thumbnail';
    return authResourceUrl(
      `${process.env.REACT_APP_FILE_SERVER}/resources/${String(resource.type).toLowerCase()}s/${resource.name}/${thumbnailName}.${resource.extension}`,
      authToken,
    );
  };

  const designHandler = () => {
    const editorUrl = `${process.env.REACT_APP_DESIGN_EDITOR}/edit/template/${template._id}/${template.tempId}`;
    const editorWindow = window.open(editorUrl, '_blank');
    if (editorWindow) editorWindow.opener = null;
  };

  const cancelHandler = () => {
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    history.push('/templates-list');
  };

  const tutorialVideoUrl = template.tutorialVideo
    ? authResourceUrl(`${process.env.REACT_APP_API_ENDPOINT}/templates/${_templateId}/tutorial-video`, authToken)
    : '';

  const uploadTutorialVideo = async event => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setTutorialVideoMessage('The tutorial video must not exceed 20 MB.');
      return;
    }
    setTutorialVideoBusy(true);
    setTutorialVideoMessage('');
    try {
      await templateService.uploadTutorialVideo(_templateId, file);
      setTutorialVideoMessage('Tutorial video uploaded successfully.');
      dispatch(templateActions.getById(_templateId));
    } catch (error) {
      setTutorialVideoMessage(error.error || error.message || 'Tutorial video upload failed.');
    } finally {
      setTutorialVideoBusy(false);
    }
  };

  const removeTutorialVideo = async () => {
    setTutorialVideoBusy(true);
    setTutorialVideoMessage('');
    try {
      await templateService.deleteTutorialVideo(_templateId);
      setTutorialVideoMessage('Tutorial video removed.');
      dispatch(templateActions.getById(_templateId));
    } catch (error) {
      setTutorialVideoMessage(error.error || error.message || 'Unable to remove tutorial video.');
    } finally {
      setTutorialVideoBusy(false);
    }
  };

  return (
    <Container fluid className="main-content-container px-4">
      {sendUpdate === 2 && <span className="alertSuccess">Update Successful</span>}
      {sendUpdate === -1 && <span className="alertDanger">Update Failed!</span>}
      <Row noGutters className="page-header py-4">
        <PageTitle sm="4" title="Edit Template" subtitle="Templates" className="text-sm-left" />
      </Row>

      <Tabs activeKey={activeTab} id="template-edit-tabs" onSelect={setActiveTab}>
        <Tab eventKey="details" title="Details">
          <form className={loading === 'loading' ? 'blur' : ''} onSubmit={formik.handleSubmit}>
            <Row>
              <Col lg="4" md="2" className="previewHolder">
                <div>
                  <span>{`Temp No.: ${_.get(template, 'tempId', '-')}`}</span>
                  <img
                    src={authResourceUrl(`${process.env.REACT_APP_FILE_SERVER}/resources/templates/${template._id}/thumbnail.png`, authToken)}
                    className="editTemplatePreview"
                    alt={template.name || 'Template preview'}
                  />
                  <span>{`${_.get(template, 'size.width', '-')} x ${_.get(template, 'size.height', '-')}`}</span>
                </div>
              </Col>
              <Col>
                <Row>
                  <Col>
                    <span>Name:</span>
                    <input id="templateName" name="name" required value={formik.values.name} onChange={formik.handleChange} />
                  </Col>
                  <Col>
                    <span>Grade:</span>
                    <select id="gradeCode" value={gradeCode} onChange={event => setGradeCode(event.currentTarget.value)}>
                      {grades.map(option => (
                        <option key={option.code} value={option.code}>{`${option.title} (1600x1600)`}</option>
                      ))}
                    </select>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <span>Subject:</span>
                    <select value={subjectCode} onChange={event => { setSubjectCode(event.target.value); setTopicCodes([]); }}>
                      <option value="">Not assigned</option>
                      {subjects.map(subject => <option key={subject.code} value={subject.code}>{subject.title}</option>)}
                    </select>
                  </Col>
                  <Col>
                    <span>Learning objective:</span>
                    <input name="learningObjective" value={formik.values.learningObjective} onChange={formik.handleChange} />
                  </Col>
                  <Col lg="3">
                    <span>Estimated minutes:</span>
                    <input type="number" min="1" max="600" name="estimatedMinutes" value={formik.values.estimatedMinutes} onChange={formik.handleChange} />
                  </Col>
                </Row>
                {topics.length > 0 && (
                  <Row><Col><span>Topics:</span><div>{topics.map(topic => (
                    <label key={topic.code} className="mr-3">
                      <input type="checkbox" checked={topicCodes.includes(topic.code)} onChange={() => setTopicCodes(current => current.includes(topic.code) ? current.filter(code => code !== topic.code) : [...current, topic.code])} />{' '}
                      {topic.title}
                    </label>
                  ))}</div></Col></Row>
                )}
                <Row>
                  <Col>
                    <span>Owner:</span>
                    <input id="tempId" name="tempId" value={template.group || ''} disabled readOnly />
                  </Col>
                  <Col>
                    <span>Value:</span>
                    <select id="price" name="price" value={formik.values.price} onChange={formik.handleChange}>
                      <option value="0">Free</option>
                      <option value="1">Premium</option>
                    </select>
                  </Col>
                  <CheckPermission resource="template" action={["update"]} allowed="invisible">
                    <Col>
                      <span>Status:</span>
                      <select className={formik.values.invisible === 'true' ? 'danger' : ''} id="invisible" name="invisible" value={formik.values.invisible} onChange={formik.handleChange}>
                        <option value="false">Publish</option>
                        <option value="true">Not Publish</option>
                      </select>
                    </Col>
                  </CheckPermission>
                </Row>
                <Row>
                  <Col>
                    <span>Description:</span>
                    <input id="description" name="description" value={formik.values.description} onChange={formik.handleChange} />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <span>Tags (En):</span>
                    <textarea id="tags" name="tags" value={formik.values.tags} onChange={formik.handleChange} />
                  </Col>
                  <Col>
                    <span>Tags (Fa):</span>
                    <textarea id="tagsFa" name="tagsFa" value={formik.values.tagsFa} onChange={formik.handleChange} />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <span>Lesson tutorial video (MP4 H.264/AAC, maximum 60 seconds and 20 MB):</span>
                    <input type="file" accept="video/mp4,.mp4" disabled={tutorialVideoBusy} onChange={uploadTutorialVideo} />
                    {tutorialVideoMessage && <div>{tutorialVideoMessage}</div>}
                    {tutorialVideoUrl && (
                      <div style={{ marginTop: '10px' }}>
                        <video key={String(_.get(template, 'tutorialVideo.updatedAt', ''))} controls preload="metadata" src={tutorialVideoUrl} style={{ width: '100%', maxWidth: '520px' }}>
                          Your browser does not support HTML video.
                        </video>
                        <div>
                          <span>{`Duration: ${_.get(template, 'tutorialVideo.duration', '-')} seconds`}</span>
                          <Button type="button" variant="danger" disabled={tutorialVideoBusy} onClick={removeTutorialVideo}>Remove tutorial video</Button>
                        </div>
                      </div>
                    )}
                  </Col>
                </Row>
                <Row noGutters className="buttonHolder">
                  <Col>
                    <Button type="submit" variant="success">Update</Button>
                    {template.tempId != null && <Button type="button" className="tableTempAction" onClick={designHandler}>Design</Button>}
                    <Button type="button" variant="danger" onClick={cancelHandler}>Cancel</Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </form>
        </Tab>

        <Tab eventKey="resources" title="Resources">
          <div id="hBtnHolder" className="hBtnHolder" />
          <AddResource uploadBtn="hBtnHolder" tempId={template.tempId} templateId={_templateId} education={template.education} lite />

          <div className={`thumbsBox ${resourcesLoading ? 'blur' : ''}`}>
            {resourcesError && <span className="alertDanger">{resourcesError}</span>}
            {!resourcesLoading && !resourcesError && templateResources.length === 0 && (
              <span>No resources are referenced by this template.</span>
            )}
            {groupedResources.map(([type, resources]) => (
              <React.Fragment key={type}>
                <span>{`${type}s (${resources.length})`}</span>
                {resources.map(resource => (
                  <div id={resource._id} key={resource._id} className="thumbsBoxHolder">
                    <i
                      onClick={() => setDeleteResource({ type: resource.type, id: resource._id, alias: resource.alias || resource.name })}
                      className="material-icons deleteIcon"
                    >&#xe92b;</i>
                    <div className="thumbHolder">
                      <img
                        title={resource.alias || resource.name}
                        className="thumb"
                        src={resourceThumbnail(resource)}
                        alt={resource.alias || resource.name}
                      />
                    </div>
                    <span>{resource.alias || resource.name}</span>
                  </div>
                ))}
                <hr />
              </React.Fragment>
            ))}
          </div>

          {deleteResource && (
            <div className="vModal">
              <div className="vModalContent">
                <div className="vModalHeader">Please Confirm</div>
                <div className="vModalBody">Do you want to delete "<b>{deleteResource.alias}</b>"?<br />This action cannot be undone.</div>
                <div className="vModalFooter">
                  <Button className="dangerBtn" onClick={() => dispatch(resourceActions.deleteResource(deleteResource.type, deleteResource.id))}>Delete</Button>
                  <Button onClick={() => setDeleteResource(null)}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {deleteMessage && (
            <div className="vModal">
              <div className="vModalContent">
                <div className="vModalBody">{deleteMessage}</div>
                <div className="vModalFooter">
                  <Button onClick={() => setDeleteMessage('')}>Close</Button>
                </div>
              </div>
            </div>
          )}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default TemplateEdit;
